import { MouseEvent, ReactNode } from 'react';

import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import styled from 'styled-components';

import { Card } from '@components/card';
import { DragHandle } from '@components/drag-and-drop';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { AttributeFieldType } from 'api/attributes/requests';

import { AttributeIcon, attributeTextMap, categoryColors, fieldTypeConfig } from './common';

type FieldCardProps = {
  attributeType: AttributeFieldType;
  title: string;
  subtitle?: string;
  dragAttributes?: DraggableAttributes;
  dragListeners?: SyntheticListenerMap;
  rightContent?: ReactNode;
  isActive?: boolean;
  hasTypeColor?: boolean;
  textSize?: 12 | 14;
  onClick?: () => void;
  onMouseDown?: (event: MouseEvent) => void;
};

const stopPropagation = (event: MouseEvent) => {
  event.stopPropagation();
};

export const FieldCard = ({
  attributeType,
  title,
  subtitle,
  dragAttributes,
  dragListeners,
  rightContent,
  isActive,
  hasTypeColor = true,
  textSize = 12,
  onClick,
  onMouseDown,
}: FieldCardProps) => {
  const isInteractive = Boolean(onClick);
  const finalSubtitle = subtitle ?? attributeTextMap[attributeType];
  const category = hasTypeColor ? fieldTypeConfig[attributeType].category : undefined;

  return (
    <StyledFieldCard
      spaceX={4}
      spaceY={3}
      variant={hasTypeColor ? 'transparent' : 'default'}
      interactive={isInteractive}
      isActive={isActive}
      data-category={category}
      data-clickable={isInteractive}
      onClick={onClick}
      onMouseDown={onMouseDown}
    >
      <Stack orientation="horizontal" align="flex-start" space={2}>
        <IconRow $height={textSize === 12 ? 32 : 40}>
          {dragListeners && (
            <DragHandle onClick={stopPropagation} {...dragAttributes} {...dragListeners} />
          )}
          <AttributeIcon
            type={attributeType}
            color={category ? categoryColors[category] : 'elementPurple80'}
          />
        </IconRow>
        <TitleColumn>
          <Text font={`semibold${textSize}`} text={title} />
          <Text font={`regular${textSize}`} text={finalSubtitle} />
        </TitleColumn>
        {rightContent}
      </Stack>
    </StyledFieldCard>
  );
};

const StyledFieldCard = styled(Card)({
  '&[data-category="inputs"]': {
    background: theme.color.orangeTrans5,
  },
  '&[data-category="selections"]': {
    background: theme.color.blueTrans5,
  },
  '&[data-clickable="true"]': {
    '&[data-category="inputs"]:hover': {
      background: theme.color.orangeTrans10,
    },
    '&[data-category="selections"]:hover': {
      background: theme.color.blueTrans10,
    },
  },
});

const TitleColumn = styled.div({
  flex: 1,
  minWidth: 0,
});

const IconRow = styled.div<{ $height: number }>(({ $height }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(8),
  height: pxToRem($height),
}));
